import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import ProductsPage from './pages/ProductsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import './App.css';

// Suppress MetaMask/wallet extension errors globally
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    const msg = event.message || '';
    if (msg.includes('MetaMask') || msg.includes('ethereum') || msg.includes('chrome-extension') || msg.includes('wallet')) {
      event.preventDefault();
      console.warn('Extension error suppressed:', msg);
    }
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason?.message || String(event.reason) || '';
    if (reason.includes('MetaMask') || reason.includes('ethereum') || reason.includes('chrome-extension') || reason.includes('wallet')) {
      event.preventDefault();
      console.warn('Extension rejection suppressed:', reason);
    }
  });
}

// Lazy load non-critical pages
const MarketplacesPage = lazy(() => import('./pages/MarketplacesPage'));
const MyStorePage = lazy(() => import('./pages/MyStorePage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TutorialsPage = lazy(() => import('./pages/TutorialsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const PurchasePage = lazy(() => import('./pages/PurchasePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const BuyCoinsPage = lazy(() => import('./pages/BuyCoinsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const AISellerPage = lazy(() => import('./pages/AISellerPage'));
const WarehousesPage = lazy(() => import('./pages/WarehousesPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const MarketingGuidePage = lazy(() => import('./pages/MarketingGuidePage'));
const ShipmentTrackingPage = lazy(() => import('./pages/ShipmentTrackingPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const DealsPage = lazy(() => import('./pages/DealsPage'));
const LiveChatDashboard = lazy(() => import('./pages/LiveChatDashboard'));
const BulkUploadPage = lazy(() => import('./pages/BulkUploadPage'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function LazyFallback() {
  return <div className="flex items-center justify-center min-h-[40vh]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
}

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <Router>
            <ScrollToTop />
            <AppRouter />
            <Toaster position="top-center" richColors />
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <GoogleAuthCallback />;
  }
  return (
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
              <Route path="/buy" element={<Layout />}>
                <Route index element={<PurchasePage />} />
              </Route>
              <Route element={<Layout />}>
                <Route path="/" element={<LandingOrPurchase />} />
                <Route path="/marketplaces" element={<MarketplacesPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/products/:marketplaceId" element={<ProductsPage />} />
                <Route path="/my-store" element={<MyStorePage />} />
                <Route path="/orders" element={<OrdersPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/tutorials" element={<TutorialsPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/buy-coins" element={<BuyCoinsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/product/:productId" element={<ProductDetailPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/ai-seller" element={<AISellerPage />} />
                <Route path="/warehouses" element={<WarehousesPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/shipments" element={<ShipmentTrackingPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/deals" element={<DealsPage />} />
                <Route path="/marketing-guide" element={<MarketingGuidePage />} />
                <Route path="/admin/live-chat" element={<LiveChatDashboard />} />
                <Route path="/admin/bulk-upload" element={<BulkUploadPage />} />
              </Route>
            </Routes>
          </Suspense>
  );
}

// Google OAuth Callback Handler
function GoogleAuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = React.useRef(false);

  React.useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = hash.split('session_id=')[1]?.split('&')[0];
    if (!sessionId) { navigate('/login'); return; }

    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/auth/google/session?session_id=${sessionId}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.token) {
          localStorage.setItem('auth_token', data.token);
          window.location.href = '/dashboard';
        } else {
          navigate('/login');
        }
      })
      .catch(() => navigate('/login'));
  }, []);

  return <LazyFallback />;
}

function LandingOrPurchase() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('ref')) {
    return <Suspense fallback={<LazyFallback />}><PurchasePage /></Suspense>;
  }
  return <LandingPage />;
}

export default App;
